import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, MapPin, Send, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import SEO, { breadcrumbSchema } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } },
};
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! I'll be in touch soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Failed to send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="min-h-screen bg-[#FDFBF7]">
      <SEO
        title="Contact"
        path="/contact"
        description="Get in touch with Dr. Erika Tajra. Send a message or enquiry about personalised health consultations, nutrigenomics, and longevity programmes."
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ])}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div variants={stagger} initial="hidden" animate="visible">
            <motion.p variants={fadeUp} className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">
              Contact
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] text-[#2D2D2D] mb-6">
              Get in Touch
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg font-light leading-relaxed text-[#6B6B6B] max-w-2xl">
              Have a question? I'd love to hear from you.
              Send a message below and I'll respond within 24 hours.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 md:pb-36">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 lg:gap-20">

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:col-span-2 space-y-8"
            >
              <div>
                <h2 className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-6">
                  Details
                </h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 border border-[#E6DCCA] flex items-center justify-center flex-shrink-0">
                      <Mail size={16} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-xs font-medium tracking-widest uppercase text-[#6B6B6B] mb-1">Email</p>
                      <a
                        href="mailto:hello@drerikatajra.com"
                        data-testid="contact-email-link"
                        className="text-base font-light text-[#2D2D2D] hover:text-[#D4AF37] transition-colors"
                      >
                        hello@drerikatajra.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 border border-[#E6DCCA] flex items-center justify-center flex-shrink-0">
                      <MapPin size={16} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-xs font-medium tracking-widest uppercase text-[#6B6B6B] mb-1">Location</p>
                      <p className="text-base font-light text-[#2D2D2D]">London, United Kingdom</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-[rgba(45,45,45,0.08)]">
                <p className="text-sm font-light text-[#6B6B6B] leading-relaxed mb-4">
                  Ready to apply for a consultation?
                </p>
                <Link
                  to="/apply"
                  data-testid="contact-book-link"
                  className="btn-gold inline-flex items-center gap-2 text-sm"
                >
                  Apply Now <ArrowRight size={14} />
                </Link>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="lg:col-span-3"
            >
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs font-medium tracking-widest uppercase text-[#6B6B6B] mb-2 block">
                      Name
                    </label>
                    <Input
                      data-testid="contact-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="bg-white border-[#E6DCCA] h-12"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium tracking-widest uppercase text-[#6B6B6B] mb-2 block">
                      Email
                    </label>
                    <Input
                      data-testid="contact-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="bg-white border-[#E6DCCA] h-12"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium tracking-widest uppercase text-[#6B6B6B] mb-2 block">
                    Message
                  </label>
                  <Textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="bg-white border-[#E6DCCA] min-h-[160px]"
                    placeholder="Your question or enquiry..."
                  />
                </div>
                <div>
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={sending}
                    className="btn-gold inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    {sending ? "Sending..." : "Send Message"} <Send size={14} />
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
